import { z } from "zod";
import {
	AdminListInputSchema,
	adminItemOutput,
	adminListOutput,
} from "./common";

// =============
// Admin Partner Payout Schemas
// =============

/**
 * Partner payout status enum.
 */
export const PartnerPayoutStatusSchema = z.enum([
	"queued",
	"charging",
	"awaiting_action",
	"charge_failed",
	"charged",
	"transferring",
	"paid",
	"needs_attention",
	"canceled",
]);
export type PartnerPayoutStatus = z.infer<typeof PartnerPayoutStatusSchema>;

/**
 * Admin partner payout schema.
 */
export const AdminPartnerPayoutSchema = z.object({
	id: z.string(),
	partnerId: z.string(),
	appId: z.string(),
	appOwnerId: z.string(),
	amountCentsUSD: z.number().int(),
	stripeTransferId: z.string().nullable(),
	stripePaymentIntentId: z.string().nullable(),
	destinationStripeAccountId: z.string(),
	sourceStripeAccountId: z.string(),
	idempotencyHash: z.string(),
	processingAt: z.coerce.date().nullable(),
	lastError: z.string().nullable(),
	createdAt: z.coerce.date(),
	paidAt: z.coerce.date().nullable(),
	status: PartnerPayoutStatusSchema,
});
export type AdminPartnerPayout = z.infer<typeof AdminPartnerPayoutSchema>;

/**
 * Admin partner payout list input with filters.
 */
export const AdminPartnerPayoutListInputSchema = AdminListInputSchema.extend({
	filter: z
		.object({
			appId: z.string().optional(),
			partnerId: z.string().optional(),
			appOwnerId: z.string().optional(),
			status: PartnerPayoutStatusSchema.optional(),
			createdAfter: z.coerce.date().optional(),
			createdBefore: z.coerce.date().optional(),
		})
		.optional(),
});
export type AdminPartnerPayoutListInput = z.infer<
	typeof AdminPartnerPayoutListInputSchema
>;

/**
 * Admin partner payout list output.
 */
export const AdminPartnerPayoutListOutputSchema = adminListOutput(
	AdminPartnerPayoutSchema,
);
export type AdminPartnerPayoutListOutput = z.infer<
	typeof AdminPartnerPayoutListOutputSchema
>;

/**
 * Admin partner payout single item output.
 */
export const AdminPartnerPayoutOutputSchema = adminItemOutput(
	AdminPartnerPayoutSchema,
);
export type AdminPartnerPayoutOutput = z.infer<
	typeof AdminPartnerPayoutOutputSchema
>;

/**
 * Admin partner payout create input.
 */
export const AdminPartnerPayoutCreateInputSchema = z.object({
	partnerId: z.string(),
	appId: z.string(),
	appOwnerId: z.string(),
	amountCentsUSD: z.number().int(),
	destinationStripeAccountId: z.string(),
	sourceStripeAccountId: z.string(),
	status: PartnerPayoutStatusSchema,
});
export type AdminPartnerPayoutCreateInput = z.infer<
	typeof AdminPartnerPayoutCreateInputSchema
>;

/**
 * Admin partner payout update input.
 */
export const AdminPartnerPayoutUpdateInputSchema = z.object({
	id: z.string(),
	status: PartnerPayoutStatusSchema.optional(),
	stripeTransferId: z.string().nullable().optional(),
	lastError: z.string().nullable().optional(),
	paidAt: z.coerce.date().nullable().optional(),
});
export type AdminPartnerPayoutUpdateInput = z.infer<
	typeof AdminPartnerPayoutUpdateInputSchema
>;
